import type { AxiosInstance } from 'axios';

import type { VerificationChannel } from '@identity/domain/types';

import { AUTH_ENDPOINTS } from './endpoints';
import type { AccountStatusApiResponse, ConsumeMagicLinkApiResponse, SendLinkApiResponse } from './types';

/** Body of the password write. `currentPassword` travels only when the caller has one to cite. */
export type CreatePasswordRequest = {
  password: string;
  currentPassword?: string;
};

/** What the status call accepts. Both are optional and independent of each other. */
export type GetStatusOptions = {
  signal?: AbortSignal;
  /**
   * Resolves the answer against one verification instead of against the account. With it the phone
   * requirement stays pending until that link is opened, which is what lets a wait watch a value
   * that is already verified.
   */
  verificationUuid?: string;
};

/** HTTP client type for the auth endpoints. */
export type AuthHttpClientType = {
  consumeMagicLink: (magicLinkToken: string) => Promise<ConsumeMagicLinkApiResponse>;
  getStatus: (options?: GetStatusOptions) => Promise<AccountStatusApiResponse>;
  createPassword: (input: CreatePasswordRequest) => Promise<void>;
  requestPasswordRecovery: (email: string) => Promise<void>;
  sendLink: (input: { value: string; channel: VerificationChannel }) => Promise<SendLinkApiResponse>;
};

/**
 * Creates the HTTP client for the auth endpoints. Only the link send answers with a body, and what
 * it answers names the operation, never the state: the wizard still derives its step from the
 * requirement map.
 */
export const createAuthHttpClient = (props: { httpClientIdentity: AxiosInstance }): AuthHttpClientType => {
  const { httpClientIdentity } = props;

  /**
   * The only call that sends its token explicitly: the single-use one from the magic link, which is
   * never stored. The interceptor leaves the header alone so a new link is never consumed with the
   * token of a wizard already open in another tab.
   */
  const consumeMagicLink = async (magicLinkToken: string): Promise<ConsumeMagicLinkApiResponse> => {
    const response = await httpClientIdentity.post<ConsumeMagicLinkApiResponse>(
      AUTH_ENDPOINTS.LINK_CONSUME,
      undefined,
      {
        headers: { Authorization: `Bearer ${magicLinkToken}` },
      },
    );

    return response.data;
  };

  /** The param is left out when there is no uuid, so the general query keeps its shape of today. */
  const getStatus = async (options?: GetStatusOptions): Promise<AccountStatusApiResponse> => {
    const response = await httpClientIdentity.get<AccountStatusApiResponse>(AUTH_ENDPOINTS.STATUS, {
      signal: options?.signal,
      ...(options?.verificationUuid ? { params: { verification_uuid: options.verificationUuid } } : {}),
    });

    return response.data;
  };

  /** The key is left out when there is no value, so the body is never `{ currentPassword: undefined }`. */
  const createPassword = async (input: CreatePasswordRequest): Promise<void> => {
    const { password, currentPassword } = input;

    await httpClientIdentity.post(AUTH_ENDPOINTS.PASSWORD, {
      password,
      ...(currentPassword ? { currentPassword } : {}),
    });
  };

  /**
   * The one call of this domain that must go out with no credential. It is sent through the open
   * instance, so there is no interceptor here to switch off.
   */
  const requestPasswordRecovery = async (email: string): Promise<void> => {
    await httpClientIdentity.post(AUTH_ENDPOINTS.PASSWORD_RECOVERY, { email });
  };

  /** An accepted send with no body resolves an empty object: three of the four flows read nothing. */
  const sendLink = async (input: { value: string; channel: VerificationChannel }): Promise<SendLinkApiResponse> => {
    const response = await httpClientIdentity.post<SendLinkApiResponse>(AUTH_ENDPOINTS.LINK_SEND, {
      channel: input.channel,
      value: input.value,
    });

    return response.data ?? {};
  };

  return { consumeMagicLink, getStatus, createPassword, requestPasswordRecovery, sendLink };
};
